import React, { useEffect, useState, useCallback } from "react";
import { Loader2, ChevronLeft, ChevronRight, Phone, Mail, CalendarDays, List, X, BellRing } from "lucide-react";
import { http } from "@/hooks/useAdmin";
import { toast } from "sonner";

const STATUS = [
  { key: "scheduled", label: "Scheduled", cls: "bg-royal/10 text-royal border-royal/20" },
  { key: "completed", label: "Completed", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  { key: "no_show", label: "No-show", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  { key: "cancelled", label: "Cancelled", cls: "bg-slate-100 text-slate-600 border-slate-200" },
];
const cls = (s) => (STATUS.find((x) => x.key === s) || STATUS[0]).cls;
const iso = (d) => d.toISOString().slice(0, 10);
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const startOfWeek = (d) => { const x = new Date(d); const day = (x.getDay() + 6) % 7; x.setDate(x.getDate() - day); x.setHours(0, 0, 0, 0); return x; };
const fmtDay = (d) => d.toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" });

const StatusSelect = ({ a, onChange }) => (
  <select data-testid={`appt-status-${a.id}`} value={a.status} onChange={(e) => onChange(a.id, { status: e.target.value })}
    className={`cursor-pointer rounded-full border px-3 py-1.5 text-xs font-semibold outline-none focus:ring-2 focus:ring-royal/20 ${cls(a.status)}`}>
    {STATUS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
  </select>
);

const Reschedule = ({ a, onSave, onClose }) => {
  const [date, setDate] = useState(a.date);
  const [slots, setSlots] = useState([]);
  const [start, setStart] = useState(a.start);
  const [err, setErr] = useState("");
  useEffect(() => { http.get("/booking/slots", { params: { date } }).then(({ data }) => setSlots(data.slots)); }, [date]);
  const save = async () => {
    setErr("");
    try { await onSave(a.id, { date, start }); onClose(); } catch (e) { setErr(e.response?.data?.detail || "Could not reschedule"); }
  };
  return (
    <div data-testid="appt-reschedule" className="mt-3 rounded-xl border border-navy/10 bg-lightbg p-3">
      <div className="flex flex-wrap items-center gap-2">
        <input type="date" data-testid="appt-reschedule-date" value={date} onChange={(e) => setDate(e.target.value)} className="rounded-lg border border-navy/10 px-2 py-1.5 text-sm outline-none focus:border-royal" />
        <select data-testid="appt-reschedule-time" value={start} onChange={(e) => setStart(e.target.value)} className="rounded-lg border border-navy/10 px-2 py-1.5 text-sm outline-none focus:border-royal">
          {slots.map((s) => <option key={s.start} value={s.start} disabled={!s.available && !(date === a.date && s.start === a.start)}>{s.label}{!s.available && !(date === a.date && s.start === a.start) ? " (booked)" : ""}</option>)}
          {!slots.length && <option value={start}>{start}</option>}
        </select>
        <button data-testid="appt-reschedule-save" onClick={save} className="rounded-lg bg-navy px-3 py-1.5 text-sm font-semibold text-white">Save</button>
        <button onClick={onClose} aria-label="Cancel" className="text-ink/40 hover:text-navy"><X className="h-4 w-4" /></button>
      </div>
      {err && <p className="mt-2 text-xs text-red-500">{err}</p>}
    </div>
  );
};

const ApptCard = ({ a, onPatch, onRemind, isToday }) => {
  const [resched, setResched] = useState(false);
  const [notes, setNotes] = useState(a.notes || "");
  const [sending, setSending] = useState(false);
  const remind = async () => { setSending(true); try { await onRemind(a.id); } finally { setSending(false); } };
  return (
    <div data-testid={`appt-card-${a.id}`} className={`rounded-2xl border bg-white p-4 ${isToday ? "border-gold/50 ring-2 ring-gold/15" : "border-navy/[0.08]"}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-display text-base font-bold text-navy">{a.time_label} <span className="ml-1 text-xs font-medium text-ink/50">{a.date}</span></p>
          <p className="mt-0.5 text-sm text-navy/80">{a.name} · {a.topic}</p>
          <p className="mt-1 flex flex-wrap gap-x-3 text-xs text-ink/55">
            <span>{a.mode_label}</span>
            {a.reminder_sent_at && (a.reminder_status === "undeliverable"
              ? <span data-testid={`appt-reminded-${a.id}`} className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 font-semibold text-amber-700"><BellRing className="h-3 w-3" /> Email undeliverable</span>
              : <span data-testid={`appt-reminded-${a.id}`} className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 font-semibold text-emerald-700"><BellRing className="h-3 w-3" /> Reminder sent</span>)}
            <a href={`tel:${a.mobile}`} className="flex items-center gap-1 hover:text-navy"><Phone className="h-3 w-3" /> {a.mobile}</a>
            <a href={`mailto:${a.email}`} className="flex items-center gap-1 hover:text-navy"><Mail className="h-3 w-3" /> {a.email}</a>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <StatusSelect a={a} onChange={onPatch} />
          <button data-testid={`appt-reschedule-${a.id}`} onClick={() => setResched((v) => !v)} className="rounded-full border border-navy/10 px-3 py-1.5 text-xs font-semibold text-navy hover:border-navy/40">Reschedule</button>
          {a.status === "scheduled" && (
            <button data-testid={`appt-remind-${a.id}`} onClick={remind} disabled={sending} title="Email the client a reminder now" className="inline-flex items-center gap-1 rounded-full border border-navy/10 px-3 py-1.5 text-xs font-semibold text-navy hover:border-navy/40 disabled:opacity-50">
              {sending ? <Loader2 className="h-3 w-3 animate-spin" /> : <BellRing className="h-3 w-3" />} Remind
            </button>
          )}
        </div>
      </div>
      {resched && <Reschedule a={a} onSave={onPatch} onClose={() => setResched(false)} />}
      <textarea data-testid={`appt-notes-${a.id}`} value={notes} onChange={(e) => setNotes(e.target.value)} onBlur={() => notes !== (a.notes || "") && onPatch(a.id, { notes })}
        placeholder="Meeting notes..." rows={1} className="mt-3 w-full resize-y rounded-xl border border-navy/10 bg-lightbg/60 px-3 py-2 text-sm outline-none focus:border-royal" />
    </div>
  );
};

const WeekView = ({ appts, weekStart, todayStr }) => {
  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-7" data-testid="appt-week">
      {days.map((d) => {
        const ds = iso(d);
        const list = appts.filter((a) => a.date === ds && a.status !== "cancelled");
        return (
          <div key={ds} className={`rounded-2xl border p-3 ${ds === todayStr ? "border-gold bg-gold/[0.06]" : "border-navy/[0.08] bg-white"}`}>
            <p className="text-xs font-semibold text-navy">{fmtDay(d)}</p>
            <div className="mt-2 space-y-1.5">
              {list.map((a) => (
                <div key={a.id} className={`rounded-lg px-2 py-1.5 text-[11px] ${a.status === "completed" ? "bg-emerald-50 text-emerald-800" : a.status === "no_show" ? "bg-amber-50 text-amber-800" : "bg-royal/10 text-navy"}`}>
                  <p className="font-semibold">{a.start} · {a.name}</p>
                  <p className="truncate opacity-70">{a.topic}</p>
                </div>
              ))}
              {!list.length && <p className="text-[11px] text-ink/35">—</p>}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export const AppointmentsView = ({ onUnauthorized }) => {
  const [data, setData] = useState({ appointments: [], counts: {}, today: iso(new Date()) });
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("list");
  const [range, setRange] = useState("upcoming");
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date()));

  const load = useCallback(async () => {
    try {
      const { data } = await http.get("/admin/appointments");
      setData(data);
    } catch (err) {
      if (err.response?.status === 401) onUnauthorized();
    } finally {
      setLoading(false);
    }
  }, [onUnauthorized]);
  useEffect(() => { load(); }, [load]);

  const patch = async (id, body) => {
    try {
      const { data: upd } = await http.patch(`/admin/appointments/${id}`, body);
      setData((d) => ({ ...d, appointments: d.appointments.map((a) => (a.id === id ? upd : a)) }));
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update appointment");
      throw err;
    }
  };

  const remind = async (id) => {
    try {
      const { data: upd } = await http.post(`/admin/appointments/${id}/remind`);
      setData((d) => ({ ...d, appointments: d.appointments.map((a) => (a.id === id ? upd : a)) }));
      if (upd.reminder_status === "undeliverable") toast.warning("Email address looks undeliverable — call or WhatsApp the client instead");
      else toast.success(`Reminder emailed to ${upd.name}`);
    } catch (err) {
      const d = err.response?.data;
      toast.error(d && !d.cloudflare_error && typeof d.detail === "string" ? d.detail : "Could not send reminder — please try again in a minute");
    }
  };

  const t = data.today;
  const list = data.appointments.filter((a) =>
    range === "today" ? a.date === t : range === "upcoming" ? a.date >= t && a.status === "scheduled" : range === "past" ? a.date < t || a.status !== "scheduled" : true);

  return (
    <div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
        {[["upcoming", "Upcoming"], ["today", "Today"], ["completed", "Completed"], ["no_show", "No-shows"], ["cancelled", "Cancelled"]].map(([k, l]) => (
          <div key={k} className="rounded-2xl border border-navy/[0.08] bg-white p-4">
            <p className="font-display text-xl font-extrabold text-navy" data-testid={`appt-count-${k}`}>{data.counts[k] ?? 0}</p>
            <p className="text-xs text-ink/55">{l}</p>
          </div>
        ))}
      </div>

      <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex gap-2" data-testid="appt-range">
          {[["upcoming", "Upcoming"], ["today", "Today"], ["past", "Past"], ["all", "All"]].map(([k, l]) => (
            <button key={k} data-testid={`appt-range-${k}`} onClick={() => setRange(k)} className={`rounded-full px-4 py-2 text-sm font-semibold ${range === k ? "bg-navy text-white" : "border border-navy/10 bg-white text-navy/75"}`}>{l}</button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="appt-view-list" onClick={() => setView("list")} className={`flex h-10 w-10 items-center justify-center rounded-full border ${view === "list" ? "border-navy bg-navy text-white" : "border-navy/10 bg-white text-navy"}`} aria-label="List view"><List className="h-4 w-4" /></button>
          <button data-testid="appt-view-week" onClick={() => setView("week")} className={`flex h-10 w-10 items-center justify-center rounded-full border ${view === "week" ? "border-navy bg-navy text-white" : "border-navy/10 bg-white text-navy"}`} aria-label="Week view"><CalendarDays className="h-4 w-4" /></button>
        </div>
      </div>

      <div className="mt-6">
        {loading ? <div className="flex justify-center py-20"><Loader2 className="h-7 w-7 animate-spin text-navy/40" /></div>
          : view === "week" ? (
            <div>
              <div className="mb-3 flex items-center justify-between">
                <button data-testid="appt-week-prev" onClick={() => setWeekStart(addDays(weekStart, -7))} className="flex h-9 w-9 items-center justify-center rounded-full border border-navy/10 bg-white"><ChevronLeft className="h-4 w-4" /></button>
                <p className="text-sm font-semibold text-navy">{fmtDay(weekStart)} – {fmtDay(addDays(weekStart, 6))}</p>
                <button data-testid="appt-week-next" onClick={() => setWeekStart(addDays(weekStart, 7))} className="flex h-9 w-9 items-center justify-center rounded-full border border-navy/10 bg-white"><ChevronRight className="h-4 w-4" /></button>
              </div>
              <WeekView appts={data.appointments} weekStart={weekStart} todayStr={t} />
            </div>
          ) : list.length === 0 ? (
            <div className="rounded-2xl border border-navy/[0.08] bg-white py-20 text-center text-sm text-ink/50" data-testid="appts-empty">No appointments in this view.</div>
          ) : (
            <div className="space-y-3" data-testid="appt-list">{list.map((a) => <ApptCard key={a.id} a={a} onPatch={patch} onRemind={remind} isToday={a.date === t && a.status === "scheduled"} />)}</div>
          )}
      </div>
    </div>
  );
};
